<script lang="ts">
  import { untrack } from 'svelte';
  import { api } from '../../lib/api/client';
  import Modal from '../../lib/components/Modal.svelte';
  import { locale, t } from '../../lib/i18n/index.ts';

  // Export downloads the member's own history as GPX or GeoJSON (spec 005), or its stays and trips with their notes as CSV (spec 005
  // 005 FR-054). Days still within history retention carry every fix along each
  // trip; older days come from the long-term timeline, simplified. The GPS
  // log is every fix the member's phones recorded, as GPX, a track per phone
  // (spec 005).
  let { onclose, date }: { onclose: () => void; date: string } = $props();

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // The range starts on the day being viewed and is the member's to change.
  let from = $state(untrack(() => date));
  let to = $state(untrack(() => date));
  let format: 'gpx' | 'geojson' | 'csv' | 'gpslog' = $state('gpx');
  let busy = $state(false);
  let error = $state('');

  type GPSLogInfo = { kept_days: number; oldest?: string; devices: { id: string; name: string; fixes: number }[] };
  let log: GPSLogInfo | null = $state(null);
  let device = $state('');

  $effect(() => {
    if (format === 'gpslog' && !log) {
      api.get<GPSLogInfo>('/me/gps-log').then((v) => (log = v)).catch((e: any) => (error = e.message));
    }
  });

  async function run() {
    busy = true;
    error = '';
    try {
      if (format === 'gpslog') {
        const q = new URLSearchParams({ from, to, tz });
        if (device) q.set('device', device);
        await api.download(`/me/gps-log/export?${q}`, `kehai-gps-log-${from}-${to}.gpx`);
      } else {
        const q = new URLSearchParams({ format, from, to, tz });
        await api.download(`/me/timeline/export?${q}`, `kehai-${from}-${to}.${format}`);
      }
      onclose();
    } catch (e: any) {
      error = e.message;
    } finally {
      busy = false;
    }
  }
</script>

<Modal title={t('timelineExportTitle')} {onclose}>
  <form class="flex flex-col gap-3" onsubmit={(e: SubmitEvent) => { e.preventDefault(); run(); }}>
    <div class="flex gap-2 items-center">
      <label class="text-sm w-12" for="ex-from">{t('timelineFrom')}</label>
      <input id="ex-from" type="date" class="input flex-1" bind:value={from} max={to} required />
    </div>
    <div class="flex gap-2 items-center">
      <label class="text-sm w-12" for="ex-to">{t('timelineTo')}</label>
      <input id="ex-to" type="date" class="input flex-1" bind:value={to} min={from} required />
    </div>
    <div class="flex gap-4 text-sm">
      <label class="flex items-center gap-1"><input type="radio" bind:group={format} value="gpx" />GPX</label>
      <label class="flex items-center gap-1"><input type="radio" bind:group={format} value="geojson" />GeoJSON</label>
      <label class="flex items-center gap-1"><input type="radio" bind:group={format} value="csv" />CSV</label>
      <label class="flex items-center gap-1"><input type="radio" bind:group={format} value="gpslog" />{t('timelineGpsLog')}</label>
    </div>
    {#if format === 'gpslog'}
      <div class="flex gap-2 items-center">
        <label class="text-sm w-12" for="ex-device">{t('timelinePhone')}</label>
        <select id="ex-device" class="input flex-1" bind:value={device}>
          <option value="">{t('timelineAllPhones')}</option>
          {#each log?.devices ?? [] as d (d.id)}
            <option value={d.id}>{t('timelineExportDevice', { phone: d.name, fixes: d.fixes })}</option>
          {/each}
        </select>
      </div>
      <p class="text-xs text-text-muted">
        {t('timelineExportGpsAbout')}
        {#if log}
          {log.oldest
            ? t('timelineExportKeptOldest', { days: log.kept_days, date: new Intl.DateTimeFormat(locale()).format(new Date(log.oldest)) })
            : t('timelineExportKept', { days: log.kept_days })}
        {/if}
      </p>
    {:else}
      <p class="text-xs text-text-muted">
        {t('timelineExportAbout')}
      </p>
    {/if}
    {#if error}<p class="text-sm" style="color: var(--theme-error);">{error}</p>{/if}
    <div class="flex justify-end gap-2">
      <button type="button" class="btn btn-secondary" onclick={onclose}>{t('appCancel')}</button>
      <button type="submit" class="btn btn-primary" disabled={busy}>{busy ? t('timelineExporting') : t('timelineExport')}</button>
    </div>
  </form>
</Modal>
